import { throttle } from "throttle-debounce";
import { useCallback, useEffect, useRef, useState } from "react";
import { useAppDispatch } from "../../../services/redux/hooks/use-dispatch";
import { useAppSelector } from "../../../services/redux/hooks/use-selector";
import {
  selectRefreshToken,
  selectSchema,
  selectSchemaValue,
  selectTemporarySchema,
} from "../redux/selectors";
import { useDrawIoMessaging } from "./use-draw-messaging";
import { deleteTemporaryScheme, setSchemaValue } from "../redux/slice";
import { useUpdateStorageValue } from "../../../app/state/storageObjectSlice/hook/use-update-storage-value";
import { useStorageSelectors } from "../../../app/state/storageObjectSlice/hook/use-storage-selectors";
import { TDownloadSchema, TSchemaExportExtensions } from "../types";
import { base64ToFile } from "../../../shared/utils/base64ToFile";
import { NormalizedStorageObject } from "../../../services/api/instances/storageApi/types/object_types";
import { SchemasValue } from "../../../services/api/instances/schemasApi/types";
import { editStorageObject } from "../../../app/state/storageObjectSlice/actions/actions";
import { downloadFile, fixEncoding, isXmlBroken, uploadFile } from "../helpers";
import { getUpdatedSchemasValue } from "../helpers/getUpdatedSchemasValue";

export const useDrawIoEditor = (
  iframeRef: React.RefObject<HTMLIFrameElement>,
) => {
  const dispatch = useAppDispatch();
  const xml = useAppSelector(selectSchema);
  const value = useAppSelector(selectSchemaValue);
  const refreshToken = useAppSelector(selectRefreshToken);
  const temporarySchema = useAppSelector(selectTemporarySchema);
  const { saveValue } = useUpdateStorageValue("schemas", setSchemaValue);
  const { selectedId, selectedObject } = useStorageSelectors("schemas");
  const [isReady, setIsReady] = useState<boolean>(false);

  const xmlRef = useRef(xml);
  const lastXmlRef = useRef<string | null>(null);
  const lastSchemas = useRef<NormalizedStorageObject<SchemasValue> | null>(
    null,
  );
  const onExportBase64Ref = useRef<((data: string) => void) | null>(null);
  const valueRef = useRef<SchemasValue | null>(null);

  useEffect(() => {
    valueRef.current = value;
  }, [value]);

  useEffect(() => {
    xmlRef.current = xml;
  }, [xml]);

  const handleInit = useCallback(() => {
    setIsReady(true);
  }, []);

  const handleExit = useCallback(() => {
    setIsReady(false);
  }, [selectedId]);

  const handleAutosaveThrottle = useCallback(
    throttle(5000, (xml: string) => {
      if (!valueRef.current) return;
      const updatedValue = getUpdatedSchemasValue(valueRef.current, {
        schema_xml: xml,
      });
      saveValue(updatedValue);
    }),
    [selectedId],
  );

  const handleAutosave = useCallback(
    (xml: string) => {
      lastXmlRef.current = xml;
      lastSchemas.current = selectedObject;
      handleAutosaveThrottle(xml);
    },
    [selectedId, handleAutosaveThrottle, selectedObject?.value],
  );

  const handleUndoChangesXml = useCallback(() => {
    if (temporarySchema && selectedId && value) {
      const currentSchema = temporarySchema[selectedId];
      loadXml(currentSchema);
      const updatedValue = getUpdatedSchemasValue(value, {
        schema_xml: currentSchema,
      });
      saveValue(updatedValue);
      dispatch(deleteTemporaryScheme({ id: selectedId }));
    }
  }, [temporarySchema, selectedId]);

  const handleExport = useCallback(
    (data?: string, format?: string, message?: TDownloadSchema) => {
      if (onExportBase64Ref.current && data && format !== "html") {
        onExportBase64Ref.current(data);
        onExportBase64Ref.current = null;
        return;
      }

      const friendlyName = selectedObject?.friendly_name;

      const file =
        format === "html"
          ? (() => {
              if (!valueRef.current) return null;
              const blob = new Blob(
                [valueRef.current.schemas_object.schema_xml],
                {
                  type: "application/xml",
                },
              );
              return new File([blob], `${friendlyName}.drawio`, {
                type: "application/xml",
              });
            })()
          : data && format
            ? base64ToFile(data, `${friendlyName}.${format}`)
            : null;

      if (!file) return;

      if (message === "download") {
        downloadFile(file);
      } else {
        uploadFile(file, dispatch);
      }
    },
    [selectedId],
  );

  const { sendAction } = useDrawIoMessaging(iframeRef, {
    onInit: handleInit,
    onExit: handleExit,
    onExport: handleExport,
    onAutosave: handleAutosave,
    onConfigure: () => {
      sendAction({
        action: "configure",
        config: {
          pageFormat: { width: 1600, height: 900 },
        },
      });
    },
  });

  const exportAsBase64 = useCallback((): Promise<string> => {
    return new Promise((resolve) => {
      onExportBase64Ref.current = resolve;
      sendAction({ action: "export", format: "png" });
    });
  }, [sendAction]);

  const loadXml = useCallback(
    (xml: string) => {
      const xmlContent = isXmlBroken(xml) ? fixEncoding(xml) : xml;
      sendAction({
        action: "load",
        xml: xmlContent,
        noExitBtn: 1,
        noSaveBtn: 1,
        saveAndExit: 0,
        autosave: 1,
      });
    },
    [sendAction],
  );

  const handleExportActions = useCallback(
    (type: TSchemaExportExtensions, message: TDownloadSchema) => {
      switch (type) {
        case "png":
          sendAction({ action: "export", format: "png", message });
          break;
        case "drawio":
          sendAction({ action: "export", format: "html", message });
          break;
        default:
          break;
      }
    },
    [sendAction],
  );

  useEffect(() => {
    if (isReady) loadXml(xmlRef.current);
  }, [isReady, selectedId]);

  useEffect(() => {
    if (!isReady || !refreshToken) return;
    loadXml(xmlRef.current);
  }, [refreshToken]);

  useEffect(() => {
    setIsReady(false);
    if (lastXmlRef.current && lastSchemas.current?.value) {
      const updatedValue = getUpdatedSchemasValue(lastSchemas.current?.value, {
        schema_xml: lastXmlRef.current,
      });
      dispatch(
        editStorageObject({
          id: lastSchemas.current?.id,
          name: lastSchemas.current?.friendly_name,
          value: updatedValue,
          service: "schemas",
        }),
      );
    }
    return () => {
      handleAutosaveThrottle.cancel();
    };
  }, [selectedId]);

  return {
    isReady,
    temporarySchema,
    setIsReady,
    loadXml,
    exportAsBase64,
    handleAutosaveThrottle,
    handleUndoChangesXml,
    handleExportActions,
  };
};
